import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiBriefcase, FiFileText, FiCheckSquare, FiGrid, FiBarChart2, FiUsers, FiLogOut, FiUser, FiChevronLeft, FiChevronRight, FiMenu, FiLayers } from 'react-icons/fi';
import './Layout.css';

const SIDEBAR_COLLAPSED_KEY = 'greencheck-sidebar-collapsed';
const MOBILE_BREAKPOINT = 768;

const Layout = ({ children }) => {
  const { user, logout, impersonating, stopImpersonation } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handle = () => setIsMobile(mql.matches);
    handle();
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch (_) {}
  }, [collapsed]);

  const toggleSidebar = () => setCollapsed((c) => !c);
  const openMobileMenu = () => setMobileMenuOpen(true);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleSidebarToggleClick = () => {
    if (isMobile) closeMobileMenu();
    else toggleSidebar();
  };

  const isAdmin = user?.role === 'admin_sistema' || user?.role === 'admin_empreendimento';
  const menuItems = [
    { path: '/home', icon: FiHome, label: 'Dashboard' },
    { path: '/empreendimentos', icon: FiBriefcase, label: 'Empreendimentos' },
    ...(isAdmin ? [{ path: '/grupos-empresariais', icon: FiLayers, label: 'Grupos empresariais' }] : []),
    { path: '/licencas', icon: FiFileText, label: 'Licenças' },
    { path: '/processos-licenciamento', icon: FiLayers, label: 'Processos de licenciamento' },
    { path: '/tarefas', icon: FiCheckSquare, label: 'Tarefas' },
    { path: '/tarefas/kanban', icon: FiGrid, label: 'Kanban' },
    { path: '/documentos', icon: FiFileText, label: 'Documentos' },
    { path: '/relatorios', icon: FiBarChart2, label: 'Relatórios' },
    ...(impersonating ? [] : [
      { path: '/configuracoes', icon: FiUsers, label: 'Configurações' },
      { path: '/admin/usuarios', icon: FiUsers, label: 'Usuários' },
    ]),
  ];

  return (
    <div className={`layout ${collapsed ? 'sidebar-collapsed' : ''} ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      <header className="mobile-topbar" aria-hidden={!isMobile}>
        <button
          type="button"
          className="mobile-topbar-menu-btn"
          onClick={openMobileMenu}
          aria-label="Abrir menu"
        >
          <FiMenu />
        </button>
        <img src="/logo.png" alt="GreenCheck" className="mobile-topbar-logo" />
      </header>
      <div
        className="sidebar-backdrop"
        aria-hidden={!mobileMenuOpen}
        onClick={closeMobileMenu}
        onTouchEnd={(e) => { e.preventDefault(); closeMobileMenu(); }}
      />
      <nav className="sidebar">
        <div className="sidebar-header">
          <img
            src={collapsed ? '/logo-min.png' : '/logo.png'}
            alt="GreenCheck"
            className={`sidebar-logo ${collapsed ? 'sidebar-logo-min' : ''}`}
            onError={(e) => { if (collapsed) { e.target.onerror = null; e.target.src = '/favicon.png'; } }}
          />
          <button
            type="button"
            className="sidebar-toggle"
            onClick={handleSidebarToggleClick}
            title={isMobile ? 'Fechar menu' : (collapsed ? 'Expandir menu' : 'Recolher menu')}
            aria-label={isMobile ? 'Fechar menu' : (collapsed ? 'Expandir menu' : 'Recolher menu')}
          >
            {isMobile ? <FiChevronLeft /> : (collapsed ? <FiChevronRight /> : <FiChevronLeft />)}
          </button>
        </div>
        <div className="sidebar-menu">
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`menu-item ${isActive ? 'active' : ''}`}
                title={item.label}
              >
                <Icon />
                <span className="menu-item-label">{item.label}</span>
              </Link>
            );
          })}
        </div>
        <div className="sidebar-footer">
          {impersonating && (
            <button onClick={stopImpersonation} className="btn-impersonation" title="Voltar à conta de administrador">
              <FiUser />
              <span className="menu-item-label">Voltar ao admin</span>
            </button>
          )}
          <div className="user-info">
            <div className="user-name">{user?.nome}</div>
            <div className="user-email text-muted">{user?.email}</div>
          </div>
          <button onClick={logout} className="btn-logout" title="Sair">
            <FiLogOut />
            <span className="menu-item-label">Sair</span>
          </button>
        </div>
      </nav>
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
